import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import _ from 'lodash';
import 'react-toastify/dist/ReactToastify.css';
import { format } from 'date-fns';
import * as PropTypes from 'prop-types';

import SlideoutPanel from '@lwt-helix/slideout-panel';
import SchemaTable from '@lwt-helix/schema-table';
import { Button } from '@lwt-helix/buttons';

import Toast from '../../Toast/index';
import { clearingStore } from '../../../actions/AuthActions';
import { resetOperationStatus } from '../../../actions/SecurityGroupActions';
import { FilterText } from '../../../constants/Invitations';
import { mapTableData } from './UsersDirectoryFunctions';
import InviteNewUser from './InviteNewUser/InviteNewUser';
import { setStateData } from '../../common/StateHelper';
import BulkActions from './BulkActions/BulkActions';
import getSchemaSettings from './SchemaTableSettings';
import { getOffices } from '../../../api/RosterApi';
import {
    fetchUsers,
    approveInvitation,
    fetchInvitations,
    reinviteUser,
    setInactiveDate,
    updateInvitation
} from '../../../api/AuthenticationApi';
import { putSecurityGroupUsers, fetchSecurityGroups } from '../../../api/SecurityGroupApi'
import { sortFunc } from '../../common/SortUtils';
import UserForm from './slideout/UserForm';
import { getLwtUser } from '../../../functions/AuthenticationFunctions';
import { Statuses } from '../../../constants/Enums';
import {
    reinviteSuccessToast,
    approvalSuccessToast,
    reinviteErrorToast,
    approvalErrorToast,
    bulkApproveToast,
    bulkReinviteSuccessToast,
    setInactiveDateFailureToast,
    setInactiveDateSuccessToast,
    securityGroupChangedFailureToast,
    securityGroupChangedSuccessToast
} from './ToastNotifications';

const UsersDirectory = ({
                            invitations,
                            authUsers,
                            securityGroups,
                            response,
                            error,
                            isLoading,
                            approval,
                            approvalError,
                            permissions,
                            bulkReinviteResponse,
                            members,
                            offices,
                            updateInvitationResponse,
                            setInactiveDateResponse,
                            securityGroupUsersUpdateStatus
                        }) => {
    const [state, setState] = useState({ slideoutPanel: {} });

    const selectAllRows = (isSelected, filteredRowIds) => {
        setState(prevState => {
            const nextState = { ...prevState };

            nextState.tableData.forEach(c => c.selected = isSelected ? filteredRowIds.includes(c.key) : false);
            nextState.selectAll = isSelected;
            nextState.schemaTable.data = [...nextState.tableData];

            return nextState;
        });
    };

    const selectRow = (key, selected) => {
        setState(prevState => {
            const nextState = { ...prevState };
            const tableRow = nextState.tableData.find(r => r.key === key);
            tableRow.selected = selected;
            nextState.schemaTable.data = [...nextState.tableData];

            return nextState;
        });
    };

    const saveSort = (field, order) => {
        setStateData('sort', { field, order }, setState);
    };

    const setFilteredRows = filteredRowIds => {
        setStateData(
            'filteredRows',
            [...state.tableData].filter(r => filteredRowIds.includes(r.key)),
            setState
        );
    };

    const securityGroupOptions = _
        .sortBy(securityGroups, ['name'])
        .reduce((result, group) => {
            result[group.id] = group.name;
            return result;
        }, {});

    const clientId = getLwtUser()?.orgId;

    const getUserBody = activeUser => {
        const officeId = activeUser.status === 'APPROVED'
            ? activeUser.clients?.find(u => u.clientId === clientId)?.officeId
            : activeUser.officeId;
        const userOffice = officeId && offices.find(o => o.id === officeId);

        activeUser = {
            ...activeUser,
            office: userOffice?.name,
            userStatus: activeUser.clients?.find(client => client.clientId === clientId)
                .inactiveDate <= format(Date.now(), 'yyyy-MM-dd')
                ? 'INACTIVE'
                : 'ACTIVE',
            hasOffice: !!officeId,
            isStatusEnabled: activeUser.status === 'APPROVED' || activeUser.status === 'INACTIVE'
        };

        return (
            <>
                <div className="badge badge-secondary" style={{ marginRight: '.5rem' }}>
                    {FilterText[activeUser.status]}
                </div>
                <span className="text-muted">{activeUser.statusDate}</span>
                <h4 className="">{activeUser.userName || activeUser.emailAddress}</h4>
                <hr className="my-4 border-top" />
                <UserForm
                    activeUser={activeUser}
                    securityGroups={[...securityGroups]}
                    onCancel={() => toggleSlideoutPanel({})}
                    refreshTable={async () => await dispatch(fetchUsers())}
                    updateInactiveDate={updateInactiveDate}
                    updateSecurityGroupUsers={updateSecurityGroupUsers}
                    updateInvitationWithRoleId={updateInvitationWithRoleId}
                />
            </>
        )
    };

    const onClick = row => setStateData('activeUser', row, setState);

    const handleApprove = () => {
        dispatch(approveInvitation(state.activeUser.id));
        setState(prevState => {
            const nextState = { ...prevState };
            nextState.slideoutPanel = {
                ...prevState.slideoutPanel,
                header: getNavButtons(true),
                body: getUserBody(state.activeUser)
            };
            nextState.isApproveDisabled = true;
            return nextState;
        });
    };

    const handleReinvite = () => {
        dispatch(reinviteUser(state.activeUser.id));
        setState(prevState => {
            const nextState = { ...prevState };
            nextState.slideoutPanel = {
                ...prevState.slideoutPanel,
                header: getNavButtons(true),
                body: getUserBody(state.activeUser)
            };
            nextState.isReinviteDisabled = true;
            return nextState;
        })
    };

    const getNavButtons = isButtonDisabled => {
        let prevRow, nextRow;
        let dataSet = [...state.filteredRows];
        if (state.sort) {
            dataSet = dataSet.sort((a, b) => sortFunc(state.sort.field, state.sort.order, a, b));
        }
        for (const [index, r] of dataSet.entries()) {
            if (r.key === state.activeUser.key) {
                prevRow = index > 0 ? dataSet[index - 1] : null;
                nextRow = index < dataSet.length - 1 ? dataSet[index + 1] : null;
                break;
            }
        }

        return (
            <>
                <Button
                    dataLwtId="previous-user"
                    size="sm"
                    onClick={prevRow !== null ? () => onClick(prevRow) : undefined}
                    disabled={prevRow == null}
                >
                    <span className="material-icons">keyboard_arrow_left</span>
                </Button>
                <Button
                    dataLwtId="next-user"
                    size="sm"
                    onClick={nextRow ? () => onClick(nextRow) : undefined}
                    disabled={!nextRow}
                >
                    <span className="material-icons">keyboard_arrow_right</span>
                </Button>

                <Button
                    dataLwtId="approve-user"
                    size="sm"
                    hidden={state.activeUser.status !== 'PENDINGAPPROVAL'}
                    onClick={handleApprove}
                    disabled={isButtonDisabled}
                >
                    <span className="material-icons mr-1 text-success font-weight-bold">done</span>
                    Approve
                </Button>
                <Button
                    dataLwtId="reinvite-user" size="sm"
                    hidden={state.activeUser.status !== 'INVITED'}
                    onClick={handleReinvite}
                    disabled={isButtonDisabled}
                >
                    <span
                        className="material-icons mr-1 text-info"
                        style={{ transform: 'rotate(-30deg)', marginTop: '-.3em', fontSize: '140%' }}
                    >
                        send
                    </span>
                    Re-Invite
                </Button>
            </>
        )
    };

    useEffect(() => {
        state.activeUser && toggleSlideoutPanel({
            contentId: `user-info-${state.activeUser.key}`,
            header: getNavButtons(),
            body: getUserBody(state.activeUser),
            onClose: () => {
                toggleSlideoutPanel({});
                return false;
            }
        });
    }, [state.activeUser]);

    const toggleSlideoutPanel = (props) => {
        setState(prevState => {
            const nextState = { ...prevState };

            if (prevState.slideoutPanel.toggleBit) {
                nextState.activeUser = null;
            }
            nextState.slideoutPanel = {
                ...props,
                toggleBit: !prevState.slideoutPanel.toggleBit,
            };
            return nextState;
        })
    };

    const dispatch = useDispatch();
    const isManageUsers = 'U-U' in permissions;

    useEffect(() => {
        dispatch(fetchInvitations());
        dispatch(fetchUsers());
        dispatch(getOffices());
    }, []);

    const updateSecurityGroupUsers = (id, users) => {
        dispatch(putSecurityGroupUsers({ id, users }));
    };

    const updateInvitationWithRoleId = (invitationId, invitation) => {
        dispatch(updateInvitation(invitationId, invitation));
    };

    const updateInactiveDate = (isActivating, userId) => {
        dispatch(setInactiveDate(isActivating, userId, clientId));
    };

    const reinviteUserHandler = invitationId => {
        if (!isLoading) {
            dispatch(reinviteUser(invitationId));
        }
    };

    const approveInvitationHandler = invitationId => {
        if (!isLoading) {
            dispatch(approveInvitation(invitationId));
        }
    };

    useEffect(() => {
        let mappedData = [];
        if (invitations != null && authUsers != null) {
            mappedData = mapTableData(
                invitations.concat(authUsers),
                securityGroups,
                reinviteUserHandler,
                approveInvitationHandler,
                isManageUsers,
                clientId
            );
        }
        setStateData('tableData', mappedData, setState);
    }, [
        securityGroups,
        invitations,
        authUsers,
        isLoading,
        isManageUsers,
        dispatch
    ]);

    useEffect(() => {
        if (response?.isReinviteResponse) {
            reinviteSuccessToast();
            dispatch(fetchInvitations());
        } else if (approval) {
            approvalSuccessToast();
            dispatch(fetchSecurityGroups());
            dispatch(fetchInvitations());
            dispatch(fetchUsers());
            dispatch(clearingStore());
        }
        if (error?.isReinviteError) {
            reinviteErrorToast();
        } else if (approvalError) {
            approvalErrorToast();
            dispatch(clearingStore());
        }
        if (response?.isBulkApproveResponse) {
            bulkApproveToast();
            dispatch(fetchInvitations());
        } else if (bulkReinviteResponse) {
            bulkReinviteSuccessToast();
            dispatch(fetchInvitations());
            dispatch(clearingStore());
        }
        if (error?.isSetInactiveDateError) {
            setInactiveDateFailureToast();
        } else if (setInactiveDateResponse) {
            setInactiveDateSuccessToast();
            dispatch(fetchInvitations());
            dispatch(clearingStore());
        }
        if (error?.isUpdateInvitationError) {
            securityGroupChangedFailureToast();
        } else if (updateInvitationResponse) {
            securityGroupChangedSuccessToast();
            dispatch(fetchInvitations());
            dispatch(clearingStore());
        }
    }, [
        response,
        error,
        approval,
        approvalError,
        bulkReinviteResponse,
        updateInvitationResponse,
        setInactiveDateResponse
    ]);

    useEffect(() => {
        if (securityGroupUsersUpdateStatus === Statuses.FAILURE) {
            securityGroupChangedFailureToast();
        } else if (securityGroupUsersUpdateStatus === Statuses.SUCCESS) {
            securityGroupChangedSuccessToast();
            dispatch(fetchSecurityGroups());
            dispatch(resetOperationStatus);
        }
    }, [securityGroupUsersUpdateStatus]);

    useEffect(() => {
            state.tableData && setState(prevState => {
                const nextState = { ...prevState };

                nextState.filteredRows = state.filteredRows || state.tableData;
                nextState.schemaTable = {
                    ...getSchemaSettings(
                        selectAllRows,
                        selectRow,
                        securityGroupOptions,
                        onClick,
                        setFilteredRows,
                        saveSort,
                        members
                    ),
                    data: state.tableData
                };
                return nextState;
            });
        }, [state.tableData, state.selectAll, securityGroups, state.filteredRows, members, offices]
    );

    return (
        <>
            <Helmet>
                <meta name="viewport" content="width=1200, initial-scale=1, shrink-to-fit=no" />
            </Helmet>
            <div className={'container'}>
                <div className={'d-flex align-items-start'}>
                    <h3 className={'mr-auto'}>Users</h3>
                    {state.tableData && <BulkActions
                        selectedRows={state.tableData.filter(r => r.selected)}
                        isBulkApproveDisabled={!isManageUsers}
                        isBulkActionsDisabled={!state.tableData?.some(r => r.selected)}
                    />
                    }
                    <InviteNewUser />
                </div>
                <Toast />
                <div className={'content'}>
                    {state.schemaTable && <SchemaTable {...state.schemaTable} dataLwtId={'users-schema-table'} />}
                </div>
                <SlideoutPanel {...state.slideoutPanel} pageHasNavbar={true} />
            </div>
        </>
    )
};

const mapStateToProps = state => ({
    securityGroups: state.securityGroup.securityGroups,
    invitations: state.authenticationReducer.invitations,
    authUsers: state.authenticationReducer.users,
    response: state.authenticationReducer.response,
    error: state.authenticationReducer.error,
    isLoading: state.authenticationReducer.isLoading,
    approval: state.authenticationReducer.approval,
    approvalError: state.authenticationReducer.approvalError,
    permissions: state.authorizationReducer.permissions.permissions,
    bulkReinviteResponse: state.authenticationReducer.bulkReinviteResponse,
    members: state.roster.members,
    offices: state.roster.offices,
    setInactiveDateResponse: state.authenticationReducer.setInactiveDateResponse,
    updateInvitationResponse: state.authenticationReducer.updateInvitationResponse,
    securityGroupUsersUpdateStatus: state.securityGroup.usersUpdateStatus
});

UsersDirectory.propTypes = {
    invitations: PropTypes.array,
    authUsers: PropTypes.array,
    securityGroups: PropTypes.array,
    response: PropTypes.object,
    error: PropTypes.object,
    isLoading: PropTypes.bool,
    approval: PropTypes.object,
    approvalError: PropTypes.object,
    permissions: PropTypes.object,
    bulkReinviteResponse: PropTypes.object,
    members: PropTypes.array,
    offices: PropTypes.array,
    updateInvitationResponse: PropTypes.object,
    setInactiveDateResponse: PropTypes.func,
    securityGroupUsersUpdateStatus: PropTypes.number
};

export default withRouter(connect(mapStateToProps)(UsersDirectory));